import React, { useMemo, useState } from 'react';

import { ChevronLeft, ChevronRight, List, Loader2, Search } from 'lucide-react';

import useImportRelativityList from '@/api/mutations/useImportRelativityList';
import useGetRelativityDocumentCount from '@/api/queries/useGetRelativityDocumentCount';
import useGetRelativityFieldMapping from '@/api/queries/useGetRelativityFieldMapping';
import useGetRelativityLists from '@/api/queries/useGetRelativityLists';
import { RelativityListType, RelativityWorkspaceType } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Skeleton } from '@/new/components/shadcn/skeleton';

interface RelativityListsModalProps {
  workspace: RelativityWorkspaceType;
  matterId?: string; // 2.0: matter-level
  caseId?: string; // Legacy: case-level
  defaultFilenameArtifactId?: number | null;
  onSelectList: (list: RelativityListType) => void;
  onBack: () => void;
  onClose: () => void;
}

interface ListItemProps {
  list: RelativityListType;
  workspaceId: number;
  isSelected: boolean;
  onSelect: (list: RelativityListType) => void;
}

const ListItem: React.FC<ListItemProps> = ({ list, workspaceId, isSelected, onSelect }) => {
  const { data: documentCount, isLoading: isLoadingCount } = useGetRelativityDocumentCount(
    workspaceId,
    list.artifactId,
  );

  return (
    <div
      className={`group cursor-pointer rounded-md border p-2.5 transition-all duration-200 ${
        isSelected
          ? 'border-teal-500 bg-teal-500/10 ring-teal-500'
          : 'border-gray-200 hover:border-teal-500/30 hover:bg-teal-500/5'
      }`}
      onClick={() => onSelect(list)}
    >
      <div className="flex min-w-0 flex-1 items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center space-x-2.5">
          <div
            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
              isSelected ? 'bg-teal-500/20' : 'bg-gray-100 group-hover:bg-teal-500/15'
            }`}
          >
            <List className={`h-4 w-4 ${isSelected ? 'text-teal-500' : 'text-gray-600 group-hover:text-teal-500'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className={`truncate text-sm font-medium ${
                isSelected ? 'text-teal-900' : 'text-gray-900 group-hover:text-teal-900'
              }`}
              title={list.name}
            >
              {list.name}
            </h3>
            <p className="truncate text-xs text-gray-500" title={`ID: ${list.artifactId}`}>
              ID: {list.artifactId}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          {isLoadingCount ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : (
            <div className="text-sm">
              <span className="font-medium text-gray-900">{documentCount?.count?.toLocaleString() ?? '0'}</span>
              <span className="ml-1 text-gray-500">{documentCount?.count === 1 ? 'file' : 'files'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const RelativityListsModal: React.FC<RelativityListsModalProps> = ({
  workspace,
  matterId,
  caseId,
  defaultFilenameArtifactId,
  onBack,
  onClose,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedList, setSelectedList] = useState<RelativityListType | null>(null);
  const [filenameFieldArtifactId, setFilenameFieldArtifactId] = useState<number | null>(
    defaultFilenameArtifactId ?? null,
  );
  const pageSize = 5;

  // Get document count for selected list to show in button
  const { data: selectedListDocumentCount } = useGetRelativityDocumentCount(
    workspace.artifactId,
    selectedList?.artifactId ?? 0,
    !!selectedList,
  );

  // Import mutation
  const importMutation = useImportRelativityList();

  // Field mapping for filename selector
  const { data: fieldMapping } = useGetRelativityFieldMapping({
    workspaceId: workspace.artifactId,
  });

  const eligibleFilenameFields = useMemo(() => {
    if (!fieldMapping?.fieldMappings) return [];
    return Object.entries(fieldMapping.fieldMappings)
      .filter(
        ([, config]) =>
          config.enabled &&
          (config.relativityFieldType === 'Fixed-Length Text' || config.relativityFieldType === 'Long Text'),
      )
      .map(([artifactId, config]) => ({
        artifactId: parseInt(artifactId, 10),
        name: config.displayName || config.relativityFieldName,
      }));
  }, [fieldMapping]);

  // Note: Search is immediate for better UX, but could be debounced if needed

  const {
    data: listsData,
    isLoading,
    error,
  } = useGetRelativityLists({
    workspaceId: workspace.artifactId,
    page: currentPage,
    pageSize,
    search: searchQuery,
  });

  const handleSelectList = (list: RelativityListType) => {
    setSelectedList(list);
  };

  const handleConfirmSelection = async () => {
    if (selectedList) {
      try {
        await importMutation.mutateAsync({
          workspaceId: workspace.artifactId,
          listId: selectedList.artifactId,
          listName: selectedList.name,
          ...(matterId ? { matterId } : {}),
          ...(caseId ? { caseId } : {}),
          ...(filenameFieldArtifactId !== null ? { filenameFieldArtifactId } : {}),
        });

        onClose();
      } catch (error) {
        console.error('Failed to start import:', error);
        // Error is handled by the mutation's onError callback
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
    setSelectedList(null);
  };

  const totalPages = Math.ceil((listsData?.totalCount || 0) / pageSize);
  const canPrevious = currentPage > 1;
  const canNext = currentPage < totalPages;

  return (
    <div>
      <DialogHeader>
        <DialogTitle>
          <Icon className="lucide--list" />
          Select Relativity List
        </DialogTitle>
        <DialogDescription>Choose a list to import all documents from.</DialogDescription>
      </DialogHeader>

      {/* Search and Pagination */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search lists..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Inline Pagination */}
        {listsData && totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!canPrevious}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 text-sm text-gray-600">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!canNext}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div>
        {isLoading && (
          <div className="space-y-2" aria-busy="true" aria-label="Loading lists">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-gray-200 p-2.5">
                <div className="flex min-w-0 flex-1 items-center space-x-2.5">
                  <Skeleton className="h-8 w-8 flex-shrink-0 rounded-md" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-3/4 max-w-[180px] rounded" />
                    <Skeleton className="h-3 w-1/2 max-w-[100px] rounded" />
                  </div>
                </div>
                <Skeleton className="h-4 w-12 flex-shrink-0 rounded" />
              </div>
            ))}
          </div>
        )}

        {!!error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="flex items-center">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Failed to load lists</h3>
                <p className="mt-1 text-sm text-red-700">
                  {String(error instanceof Error ? error.message : 'An unknown error occurred')}
                </p>
              </div>
            </div>
          </div>
        )}

        {listsData && listsData.lists.length === 0 && !isLoading && (
          <div className="py-12 text-center">
            <List className="mx-auto h-10 w-10 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchQuery ? 'No matching lists' : 'No lists found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? 'Try adjusting your search terms.' : 'This workspace does not contain any lists.'}
            </p>
          </div>
        )}

        {listsData && listsData.lists.length > 0 && (
          <div className="space-y-2">
            {listsData.lists.map((list) => (
              <ListItem
                key={list.artifactId}
                list={list}
                workspaceId={workspace.artifactId}
                isSelected={selectedList?.artifactId === list.artifactId}
                onSelect={handleSelectList}
              />
            ))}
          </div>
        )}
      </div>

      {selectedList && eligibleFilenameFields.length > 0 && (
        <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Optional: use a metadata field as filename
          </label>
          <Select
            value={filenameFieldArtifactId?.toString() ?? 'none'}
            onValueChange={(value) => setFilenameFieldArtifactId(value === 'none' ? null : parseInt(value, 10))}
          >
            <SelectTrigger className="h-9 w-full bg-white text-sm text-gray-700">
              <SelectValue placeholder="Keep original filenames" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Keep original filenames</SelectItem>
              {eligibleFilenameFields.map((field) => (
                <SelectItem key={field.artifactId} value={field.artifactId.toString()}>
                  {field.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
        <div className="flex items-center text-sm text-gray-500">
          {selectedList && (
            <span className="font-medium text-gray-700">
              Selected: <span className="font-semibold text-gray-700">{selectedList.name}</span>
            </span>
          )}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          {selectedList && (
            <Button variant="primary" onClick={handleConfirmSelection} disabled={importMutation.isLoading}>
              {importMutation.isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting Import...
                </>
              ) : (
                `Import ${selectedListDocumentCount?.count?.toLocaleString() ?? '0'} ${selectedListDocumentCount?.count === 1 ? 'File' : 'Files'}`
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
